import { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useSelector } from 'react-redux';
import {
  MapPin, ChevronLeft, ChevronRight, User, Phone, Layers,
  Navigation, Crop, Globe, Filter, Maximize2,
  Award, BarChart3,
  Minimize2,
} from 'lucide-react';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function UserMap({ activeTab }) {
  const { locations, loading, error } = useSelector(state => state.usageLocations);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [expandedView, setExpandedView] = useState(false);
  const [openPopupKey, setOpenPopupKey] = useState(null);
  const mapRef = useRef(null);
  const markerRefs = useRef({});

  // Clear selected user when switching tabs
  useEffect(() => {
    // Reset only when switching to Telenor users map
    if (activeTab === "telco") {
      setSelectedUser(null);
      setCurrentFieldIndex(0);
      setOpenPopupKey(null);
    }
  }, [activeTab]);


  // Group users by username
  const groupedUsers = locations.reduce((acc, location) => {
    if (activeTab === "telco" && location.source !== "Telenor") return acc;
    if (activeTab === "app" && location.source !== "Main") return acc;

    const username = location.username;
    if (!acc[username]) {
      acc[username] = [];
    }
    acc[username].push(location);
    return acc;
  }, {});

  // Convert grouped users to array
  const mapUsers = Object.entries(groupedUsers)
    .map(([username, userFields]) => ({
      username,
      fields: userFields.filter(field => field.firstPoint),
      totalFields: userFields.length,
      totalAreaAcres: userFields.reduce((sum, field) => sum + (field.area_acres || 0), 0)
    }))
    .filter(user => user.fields.length > 0);

  const totalFields = mapUsers.reduce((acc, user) => acc + user.fields.length, 0);

  const totalAreaAcres = mapUsers.reduce((acc, user) =>
    acc + user.fields.reduce((sum, field) => sum + (field.area_acres || 0), 0)
    , 0);

  const avgAreaPerField = totalFields > 0 ? totalAreaAcres / totalFields : 0;


  // Navigation functions with smooth map movement
  const navigateToField = (user, fieldIndex) => {
    if (!user || !user.fields[fieldIndex]?.firstPoint) return;

    setSelectedUser(user);
    setCurrentFieldIndex(fieldIndex);

    // Close any open popup
    setOpenPopupKey(null);

    // Smoothly pan to the new field location
    const field = user.fields[fieldIndex];
    const latLng = [field.firstPoint.lat, field.firstPoint.lng];

    if (mapRef.current) {
      mapRef.current.flyTo(latLng, mapRef.current.getZoom(), {
        duration: 1.5,
        easeLinearity: 0.25
      });
    }

    // Open the popup for the new field
    const newPopupKey = `${user.username}-${fieldIndex}`;
    setOpenPopupKey(newPopupKey);

    // Trigger click on the marker after a short delay
    setTimeout(() => {
      const markerRef = markerRefs.current[newPopupKey];
      if (markerRef) {
        markerRef.openPopup();
      }
    }, 300);
  };

  const nextField = () => {
    if (selectedUser && currentFieldIndex < selectedUser.fields.length - 1) {
      navigateToField(selectedUser, currentFieldIndex + 1);
    }
  };

  const prevField = () => {
    if (currentFieldIndex > 0) {
      navigateToField(selectedUser, currentFieldIndex - 1);
    }
  };

  // Initialize map ref
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.whenReady(() => {
        // Map is ready
      });
    }
  }, []);

  // Calculate center
  const calculateCenter = () => {
    if (mapUsers.length === 0) return [30.3753, 69.3451];

    const allPoints = mapUsers.flatMap(user =>
      user.fields.map(field => field.firstPoint)
    ).filter(point => point);

    const sum = allPoints.reduce((acc, point) => {
      acc.lat += point.lat;
      acc.lng += point.lng;
      acc.count++;
      return acc;
    }, { lat: 0, lng: 0, count: 0 });

    return [
      sum.lat / sum.count || 30.3753,
      sum.lng / sum.count || 69.3451
    ];
  };

  const center = calculateCenter();

  // Custom marker colors
  const getMarkerColor = (plan) => {
    const colors = {
      'Free': '#9CA3AF',
      'Diamond': '#3B82F6',
      'Platinum': '#8B5CF6',
      'Gold': '#F59E0B'
    };
    return colors[plan] || '#9CA3AF';
  };

  // Create custom icon
  const createCustomIcon = (user) => {
    const mainColor = getMarkerColor(user.fields[0]?.plan);
    const fieldCount = user.fields.length;
    const avatarUrl = './farmer.webp'; // fallback if no avatar

    return L.divIcon({
      className: 'custom-marker',
      html: `
      <div class="relative group">
        <!-- Avatar Circle -->
        <div class="w-8 h-8 rounded-full overflow-hidden border-2"
             style="border-color: ${mainColor};">
          <img src="${avatarUrl}" alt="avatar" class="w-full h-full object-cover" />
        </div>

        <!-- Field count badge -->
        ${fieldCount > 1 ? `
        <div class="absolute -top-2 -right-2 w-5 h-5 bg-white rounded-full border-2 shadow-md flex items-center justify-center"
             style="border-color: ${mainColor}">
          <span class="text-[8px] font-bold" style="color: ${mainColor}">${fieldCount}</span>
        </div>` : ''}
      </div>
    `,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });
  };


  // Loading state
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-8">
        <div className="h-[600px] flex flex-col items-center justify-center space-y-6">
          <div className="relative">
            <div className="w-24 h-24 border-4 border-gray-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-24 h-24 border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <div className="text-center space-y-2">
            <h3 className="text-xl font-semibold text-gray-700">Loading Map Data</h3>
            <p className="text-gray-500">Fetching user locations and field information...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-gradient-to-br from-white to-red-50 rounded-2xl shadow-2xl p-8">
        <div className="h-[600px] flex flex-col items-center justify-center space-y-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xl">!</span>
            </div>
          </div>
          <div className="text-center space-y-2">
            <h3 className="text-xl font-semibold text-gray-700">Error Loading Data</h3>
            <p className="text-red-600 max-w-md">{error}</p>
            <button className="mt-4 px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-shadow">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (mapUsers.length === 0) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-8">
        <div className="h-[600px] flex flex-col items-center justify-center space-y-6">
          <div className="relative">
            <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
              <MapPin className="w-16 h-16 text-gray-300" />
            </div>
            <div className="absolute -top-2 -right-2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center">
              <Filter className="w-6 h-6 text-gray-400" />
            </div>
          </div>
          <div className="text-center space-y-3">
            <h3 className="text-2xl font-bold text-gray-700">
              No {activeTab === "app" ? "App" : "Telenor"} Users Found
            </h3>
            <p className="text-gray-500 max-w-md">
              {activeTab === "app"
                ? "Connect your app to start tracking fields. Users will appear here when data is available."
                : "Telenor users will be displayed here once the integration is active and data flows in."}
            </p>
            <div className="pt-4">
              <button className="px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:shadow-lg transition-all duration-300">
                Add Sample Data
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentField = selectedUser?.fields[currentFieldIndex];

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              {activeTab === "app" ? 'App Users' : 'Telenor Users'} Map
            </h2>
            <p className="text-gray-500 mt-1">
              Interactive visualization of {activeTab === "app" ? 'app' : 'telco'} user field locations
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-xl border border-blue-100">
              <p className="text-sm text-gray-600">Showing</p>
              <p className="text-lg font-bold text-gray-800">{mapUsers.length} users</p>
            </div>
            <button
              onClick={() => setExpandedView(!expandedView)}
              title={expandedView ? "Exit full map view" : "Expand map"}
              className={`p-3 rounded-xl border transition-all duration-300 active:scale-95
    ${expandedView
                  ? "bg-gradient-to-r from-emerald-50 to-green-100 border-green-200 hover:shadow-green-200/50"
                  : "bg-gradient-to-r from-blue-50 to-indigo-100 border-blue-200 hover:shadow-blue-200/50"
                }
  `}
            >
              {expandedView ? (
                <Minimize2 className="w-5 h-5 text-green-700" />
              ) : (
                <Maximize2 className="w-5 h-5 text-blue-700" />
              )}
            </button>

          </div>
        </div>
      </div>
      {/* Footer Stats */}
      <div className="border-t border-gray-100 p-6">
        <div className="grid grid-cols-1  md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 mb-1">Total Users</p>
                <p className="text-2xl font-bold text-gray-800">{mapUsers.length}</p>
              </div>
              <User className="w-8 h-8 text-blue-400" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 mb-1">Total Fields</p>
                <p className="text-2xl font-bold text-gray-800">
                  {mapUsers.reduce((acc, user) => acc + user.fields.length, 0)}
                </p>
              </div>
              <Layers className="w-8 h-8 text-purple-400" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border border-green-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 mb-1">Avg Fields/User</p>
                <p className="text-2xl font-bold text-gray-800">
                  {Math.round(mapUsers.reduce((acc, user) => acc + user.fields.length, 0) / mapUsers.length) || 0}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-green-400" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 mb-1">Total Area (acres)</p>
                <p className="text-2xl font-bold text-gray-800">
                  {totalAreaAcres.toFixed(2)}
                </p>
              </div>
              <Globe className="w-8 h-8 text-blue-400" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border border-green-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 mb-1">Avg Area / Field</p>
                <p className="text-2xl font-bold text-gray-800">
                  {avgAreaPerField.toFixed(2)} acres
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-green-400" />
            </div>
          </div>


          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">Data Source</p>
                <p className="text-xl font-bold text-gray-800">
                  {activeTab === "app" ? "Main App" : "Telenor"}
                </p>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activeTab === "app"
                ? "bg-green-100 text-green-600"
                : "bg-blue-100 text-blue-600"
                }`}>
                {activeTab === "app" ? "A" : "T"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row mb-50">
        {/* Map Section */}
        <div className={`${expandedView ? 'lg:w-full' : 'lg:w-2/3'} h-[500px] lg:h-[600px] relative`}>


          <MapContainer
            center={center}
            zoom={activeTab === "telco" ? 7 : 6}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
            className="rounded-lg"
            key={activeTab}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {mapUsers.map((user, userIndex) => (
              user.fields.map((field, fieldIndex) => (
                field.firstPoint && (
                  <Marker
                    key={`${user.username}-${fieldIndex}`}
                    position={[field.firstPoint.lat, field.firstPoint.lng]}
                    icon={createCustomIcon(user)}
                    eventHandlers={{
                      click: () => {
                        navigateToField(user, fieldIndex);
                      }
                    }}
                    ref={(ref) => {
                      if (ref) {
                        markerRefs.current[`${user.username}-${fieldIndex}`] = ref;
                      }
                    }}
                  >
                    <Popup
                      className="custom-popup rounded-xl shadow-2xl border-0 w-[350px] max-w-[90vw] max-h-[150px]  p-0"
                      onClose={() => setOpenPopupKey(null)}
                    >
                      <div className="flex h-full ">
                        {/* Left side - User info */}
                        <div className="w-1/2 bg-gradient-to-br from-gray-50 to-gray-100 p-3 mb-4 rounded-l-xl border-r border-gray-200">
                          <div className="flex items-start space-x-2 h-full">
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow">
                                <User className="w-5 h-5 text-white" />
                              </div>
                            </div>

                            {/* User details */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-gray-800 truncate">
                                {activeTab === "app"
                                  ? field.username?.split('@')[0] || field.username || 'User'
                                  : `+92${field.username}`}
                              </h3>

                              <div className="mt-1">
                                <div className="flex items-center">
                                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${field.plan === 'Free'
                                    ? 'bg-gray-200 text-gray-700'
                                    : field.plan === 'Diamond'
                                      ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 border border-blue-300'
                                      : 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-700 border border-purple-300'
                                    }`}>
                                    {field.plan || 'Free'}
                                  </span>
                                  <div className={`w-1.5 h-1.5 rounded-full ml-2 ${field.source === 'Main' ? 'bg-green-500' : 'bg-blue-500'
                                    }`}></div>
                                </div>

                                <div className="mt-2 flex items-center text-gray-500">
                                  <Crop className="w-3 h-3 mr-1" />
                                  <span className="text-xs truncate">
                                    {field.fieldName || `Field ${fieldIndex + 1}`}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right side - Coordinates and actions */}
                        <div className="w-1/2 p-3 bg-white rounded-r-xl">
                          {/* Coordinates */}
                          <div className="mb-3">
                            <div className="flex items-center text-gray-500 mb-1">
                              <Navigation className="w-3 h-3 mr-1" />
                              <span className="text-xs font-medium">Coordinates</span>
                            </div>
                            <div className="space-y-0.5">
                              <div className="text-xs font-mono text-gray-800 bg-gray-50 px-2 py-1 rounded">
                                Lat: {field.firstPoint.lat.toFixed(4)}
                              </div>
                              <div className="text-xs font-mono text-gray-800 bg-gray-50 px-2 py-1 rounded">
                                Lng: {field.firstPoint.lng.toFixed(4)}
                              </div>
                            </div>
                          </div>

                          {/* Action button */}
                          {/* <button
        onClick={(e) => {
          e.stopPropagation();
          navigateToField(user, fieldIndex);
        }}
        className="w-full flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow"
      >
        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        View Details
      </button> */}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )
              ))
            ))}
          </MapContainer>
        </div>

        {/* Side Panel - User Details */}
        {!expandedView && (
          <div className="lg:w-1/3 border-l border-gray-100 bg-gradient-to-b from-white to-gray-50">
            {selectedUser ? (
              <div className="h-[600px] overflow-y-auto p-6">
                {/* User Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                        <User className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">
                          {activeTab === "app"
                            ? selectedUser.fields[0]?.username?.split('@')[0] || selectedUser.fields[0]?.username || 'User'
                            : `+92${selectedUser.fields[0]?.username}`}
                        </h3>
                        <p className="text-gray-500 text-sm">User ID: {selectedUser.username.slice(0, 12)}...</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold shadow-sm ${selectedUser.fields[0]?.plan === 'Free'
                      ? 'bg-gray-100 text-gray-700 border border-gray-200'
                      : selectedUser.fields[0]?.plan === 'Diamond'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                        : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
                      }`}>
                      {selectedUser.fields[0]?.plan || 'Free'}
                    </span>
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-blue-600 mb-1">Total Fields</p>
                          <p className="text-2xl font-bold text-gray-800">{selectedUser.totalFields}</p>
                        </div>
                        <Layers className="w-8 h-8 text-blue-400" />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-purple-600 mb-1">Active Fields</p>
                          <p className="text-2xl font-bold text-gray-800">{selectedUser.fields.length}</p>
                        </div>
                        <MapPin className="w-8 h-8 text-purple-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current Field Details */}
                {currentField && (
                  <div className="space-y-6">
                    {/* Field Header */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 flex items-center justify-center">
                            <Crop className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-800">Current Field</h4>
                            <p className="text-sm text-gray-500">Field {currentFieldIndex + 1}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Source</p>
                          <span className={`px-2 py-1 rounded-full text-xs ${currentField.source === 'Main'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                            }`}>
                            {currentField.source}
                          </span>
                        </div>
                      </div>

                      {/* Field Info */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <Navigation className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">Coordinates</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-mono text-gray-800">
                              {currentField.firstPoint.lat.toFixed(6)}
                            </p>
                            <p className="text-sm font-mono text-gray-800">
                              {currentField.firstPoint.lng.toFixed(6)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <Globe className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">Field Name</span>
                          </div>
                          <span className="text-sm font-medium text-gray-800">
                            {currentField.fieldName || `Field ${currentFieldIndex + 1}`}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <Globe className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">Area (acres)</span>
                          </div>
                          <span className="text-sm font-medium text-gray-800">
                            {currentField.area_acres || "N/A"}
                          </span>
                        </div>

                      </div>
                    </div>

                    {/* Navigation Controls with Smart Dot Pagination */}
                    {selectedUser.fields.length > 1 && (
                      <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold text-gray-700">Field Navigation</h4>
                          <span className="text-xs text-gray-500">
                            {currentFieldIndex + 1} of {selectedUser.fields.length}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <button
                            onClick={prevField}
                            disabled={currentFieldIndex === 0}
                            className={`flex items-center px-4 py-2.5 rounded-xl transition-all ${currentFieldIndex === 0
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 hover:shadow-md active:scale-95'
                              }`}
                          >
                            <ChevronLeft className="w-5 h-5 mr-2" />
                            Previous
                          </button>

                          {/* Smart paginated dots */}
                          <div className="flex items-center space-x-1 px-4">
                            {(() => {
                              const totalFields = selectedUser.fields.length;
                              const maxVisibleDots = 7; // Maximum dots to show

                              if (totalFields <= maxVisibleDots) {
                                // Show all dots if less than max
                                return selectedUser.fields.map((_, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => navigateToField(selectedUser, idx)}
                                    className={`w-2 h-2 rounded-full transition-all duration-200 ${idx === currentFieldIndex
                                      ? 'bg-blue-500 w-6'
                                      : 'bg-gray-300 hover:bg-gray-400'
                                      }`}
                                  />
                                ));
                              }

                              // Calculate which dots to show
                              const halfVisible = Math.floor(maxVisibleDots / 2);
                              let startIndex = currentFieldIndex - halfVisible;
                              let endIndex = currentFieldIndex + halfVisible;

                              // Adjust if near the beginning
                              if (startIndex < 0) {
                                startIndex = 0;
                                endIndex = maxVisibleDots - 1;
                              }

                              // Adjust if near the end
                              if (endIndex >= totalFields) {
                                endIndex = totalFields - 1;
                                startIndex = endIndex - (maxVisibleDots - 1);
                              }

                              // Create dots array with ellipsis indicators
                              const dots = [];

                              // Add first dot with ellipsis if needed
                              if (startIndex > 0) {
                                dots.push(
                                  <button
                                    key="first"
                                    onClick={() => navigateToField(selectedUser, 0)}
                                    className="w-2 h-2 rounded-full bg-gray-300 hover:bg-gray-400"
                                  />
                                );

                                if (startIndex > 1) {
                                  dots.push(
                                    <span key="left-ellipsis" className="text-gray-400 text-xs px-1">
                                      ...
                                    </span>
                                  );
                                }
                              }

                              // Add visible dots
                              for (let i = startIndex; i <= endIndex; i++) {
                                dots.push(
                                  <button
                                    key={i}
                                    onClick={() => navigateToField(selectedUser, i)}
                                    className={`w-2 h-2 rounded-full transition-all duration-200 ${i === currentFieldIndex
                                      ? 'bg-blue-500 w-6'
                                      : 'bg-gray-300 hover:bg-gray-400'
                                      }`}
                                  />
                                );
                              }

                              // Add last dot with ellipsis if needed
                              if (endIndex < totalFields - 1) {
                                if (endIndex < totalFields - 2) {
                                  dots.push(
                                    <span key="right-ellipsis" className="text-gray-400 text-xs px-1">
                                      ...
                                    </span>
                                  );
                                }

                                dots.push(
                                  <button
                                    key="last"
                                    onClick={() => navigateToField(selectedUser, totalFields - 1)}
                                    className="w-2 h-2 rounded-full bg-gray-300 hover:bg-gray-400"
                                  />
                                );
                              }

                              return dots;
                            })()}
                          </div>

                          <button
                            onClick={nextField}
                            disabled={currentFieldIndex === selectedUser.fields.length - 1}
                            className={`flex items-center px-4 py-2.5 rounded-xl transition-all ${currentFieldIndex === selectedUser.fields.length - 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 hover:shadow-md active:scale-95'
                              }`}
                          >
                            Next
                            <ChevronRight className="w-5 h-5 ml-2" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h4 className="font-semibold text-gray-700 mb-4">Quick Actions</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <button className="flex flex-col items-center justify-center p-3 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-md active:scale-95 transition-all duration-200">
                          <Phone className="w-5 h-5 text-blue-600 mb-2" />
                          <span className="text-xs font-medium text-gray-700">Call User</span>
                        </button>
                        <button className="flex flex-col items-center justify-center p-3 rounded-lg bg-gradient-to-br from-green-50 to-green-100 hover:shadow-md active:scale-95 transition-all duration-200">
                          <Navigation className="w-5 h-5 text-green-600 mb-2" />
                          <span className="text-xs font-medium text-gray-700">Get Directions</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Empty State for Side Panel */
              <div className="h-[600px] flex flex-col items-center justify-center p-6 text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-6">
                  <MapPin className="w-12 h-12 text-gray-300" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a User</h3>
                <p className="text-gray-500 mb-6 max-w-xs">
                  Click on any marker on the map to view detailed user information and field navigation options.
                </p>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Award className="w-4 h-4" />
                  <span>Pro Tip: Use field navigation to browse through user's locations</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>


    </div>
  );
}